import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import DashboardLayout from "../layouts/DashboardLayout";
import "./Profile.css";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

function Profile({ toggleDarkMode }) {
  const navigate = useNavigate();
  const currentUser = localStorage.getItem("name");

  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("info");
  const [msg, setMsg] = useState({ text: "", type: "" });

  const [name, setName] = useState("");
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });

  useEffect(() => {
    fetch(`${API}/profile/${currentUser}`)
      .then(r => r.json())
      .then(data => {
        setProfile(data);
        setName(data.name || "");
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [currentUser]);

  const showMsg = (text, type) => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: "", type: "" }), 3500);
  };

  const handleSaveName = async () => {
    if (!name.trim()) return showMsg("Name cannot be empty", "error");
    setSaving(true);
    try {
      const res = await fetch(`${API}/profile/${profile._id}/update`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        localStorage.setItem("name", name.trim());
        showMsg("Name updated successfully!", "success");
      } else {
        showMsg(data.message || "Update failed", "error");
      }
    } catch {
      showMsg("Server error. Try again.", "error");
    }
    setSaving(false);
  };

  const handleChangePassword = async () => {
    const { currentPassword, newPassword, confirmPassword } = passwordForm;
    if (!currentPassword) return showMsg("Enter your current password", "error");
    if (!newPassword) return showMsg("Enter a new password", "error");
    if (newPassword.length < 6) return showMsg("Password must be at least 6 characters", "error");
    if (newPassword !== confirmPassword) return showMsg("Passwords do not match", "error");

    setSaving(true);
    try {
      const res = await fetch(`${API}/profile/${profile._id}/change-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ currentPassword, newPassword, confirmPassword })
      });
      const data = await res.json();
      if (res.ok) {
        showMsg("Password changed successfully!", "success");
        setPasswordForm({ currentPassword: "", newPassword: "", confirmPassword: "" });
      } else {
        showMsg(data.message || "Failed to change password", "error");
      }
    } catch {
      showMsg("Server error. Try again.", "error");
    }
    setSaving(false);
  };

  const getInitials = (n) =>
    n?.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2) || "";

  const roleLabel = profile?.role === "admin"
    ? "Admin"
    : profile?.role === "courier"
    ? "Courier"
    : "User";

  if (loading) return (
    <DashboardLayout toggleDarkMode={toggleDarkMode}>
      <div className="profile-loading"><div className="profile-spinner" /></div>
    </DashboardLayout>
  );

  return (
    <DashboardLayout toggleDarkMode={toggleDarkMode}>
      <div className="profile-page">

        {/* HERO */}
        <div className="profile-hero">
          <div className="profile-avatar-wrap">
            <div className="profile-avatar-ring">
              <div className="profile-avatar-inner">
                {getInitials(profile?.name)
                  ? <span className="profile-avatar-initials">{getInitials(profile?.name)}</span>
                  : (
                    <svg className="profile-avatar-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="8" r="4" />
                      <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
                    </svg>
                  )
                }
              </div>
            </div>
          </div>

          <div className="profile-hero-info">
            <div className="profile-hero-name-row">
              <h1>{profile?.name || "Your Profile"}</h1>
              <span className={`profile-role-badge role-${profile?.role}`}>{roleLabel}</span>
            </div>
            <p className="profile-hero-email">{profile?.email}</p>
          </div>
        </div>

        {/* TABS */}
        <div className="profile-tabs">
          <button
            className={`profile-tab ${activeTab === "info" ? "active" : ""}`}
            onClick={() => setActiveTab("info")}
          >
            Personal Info
          </button>
          <button
            className={`profile-tab ${activeTab === "password" ? "active" : ""}`}
            onClick={() => setActiveTab("password")}
          >
            Change Password
          </button>
        </div>

        {/* MESSAGE */}
        {msg.text && (
          <div className={`profile-msg ${msg.type}`}>
            {msg.type === "success" ? "✅" : "⚠️"} {msg.text}
          </div>
        )}

        <div className="profile-card">

          {/* TAB 1 — PERSONAL INFO */}
          {activeTab === "info" && (
            <div className="profile-form">
              <div className="profile-field">
                <label>Full Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="Your full name"
                  onKeyDown={e => { if (e.key === "Enter") handleSaveName(); }}
                />
              </div>
              <div className="profile-field">
                <label>Email Address</label>
                <input
                  value={profile?.email || ""}
                  disabled
                  className="profile-input-disabled"
                />
                <p className="profile-hint">Email address cannot be changed</p>
              </div>
              <div className="profile-field">
                <label>Phone Number</label>
                <input
                  value={profile?.phone || "—"}
                  disabled
                  className="profile-input-disabled"
                />
                <p className="profile-hint">Contact admin to update phone</p>
              </div>
              <div className="profile-field">
                <label>Account Role</label>
                <input
                  value={profile?.role || "user"}
                  disabled
                  className="profile-input-disabled"
                />
                <p className="profile-hint">Role is assigned by admin</p>
              </div>
              <button
                className="profile-save-btn"
                onClick={handleSaveName}
                disabled={saving}
              >
                {saving ? "Saving..." : "Save Name"}
              </button>
            </div>
          )}

          {/* TAB 2 — CHANGE PASSWORD */}
          {activeTab === "password" && (
            <div className="profile-form">
              <div className="profile-security-note">
                For your security, verify your current password before setting a new one.
              </div>
              <div className="profile-field">
                <label>Current Password</label>
                <input
                  type="password"
                  value={passwordForm.currentPassword}
                  onChange={e => setPasswordForm(p => ({ ...p, currentPassword: e.target.value }))}
                  placeholder="Enter your current password"
                />
              </div>
              <div className="profile-field">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={e => setPasswordForm(p => ({ ...p, newPassword: e.target.value }))}
                  placeholder="At least 6 characters"
                />
              </div>
              <div className="profile-field">
                <label>Confirm New Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={e => setPasswordForm(p => ({ ...p, confirmPassword: e.target.value }))}
                  placeholder="Repeat new password"
                  onKeyDown={e => { if (e.key === "Enter") handleChangePassword(); }}
                />
              </div>
              <button
                className="profile-save-btn"
                onClick={handleChangePassword}
                disabled={saving}
              >
                {saving ? "Changing..." : "Change Password"}
              </button>
            </div>
          )}

        </div>

        {/* MY ITEMS QUICK LINK */}
        <div className="profile-items-link">
          <p>Want to edit your listed items?</p>
          <button onClick={() => navigate("/add-item")}>
            Manage My Items →
          </button>
        </div>

      </div>
    </DashboardLayout>
  );
}

export default Profile;